import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaLock, FaToggleOn, FaTimes, FaSave } from 'react-icons/fa';
import { talukaAPI } from '../../../../utils/api';
import '../../../../styles/institutional-form.css';
import '../css/talukaCreate.css';

function TalukaCreate({ taluka = null, onClose, onSubmit }) {
  const isEdit = !!taluka;

  const [code, setCode]         = useState('');
  const [name, setName]         = useState('');
  const [isActive, setIsActive] = useState('true');
  const [errors, setErrors]     = useState({});
  const [busy, setBusy]         = useState(false);
  const [codeLoading, setCodeLoading] = useState(!isEdit);

  useEffect(() => {
    if (isEdit) {
      setCode(taluka.code || taluka.taluka_id || '');
      setName(taluka.name || '');
      setIsActive(taluka.is_active != null ? String(taluka.is_active) : 'true');
    } else {
      setCodeLoading(true);
      talukaAPI.nextCode()
        .then(r => setCode(r.data.code))
        .catch(() => setCode('TLK-???'))
        .finally(() => setCodeLoading(false));
    }
  }, [isEdit, taluka]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Taluka name is required';
    if (name.trim().length > 1 && !/^[a-zA-Z\s\-.]+$/.test(name.trim())) e.name = 'Only letters, spaces, hyphens, and periods';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = { name: name.trim(), code, is_active: isActive === 'true' };
      if (isEdit) await talukaAPI.update(taluka.id, payload);
      else        await talukaAPI.create(payload);
      Swal.fire('Success', isEdit ? 'Taluka updated.' : 'Taluka created.', 'success');
      onSubmit?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed';
      const friendly = /talukas_code_key/i.test(msg) ? 'This code already exists.' : msg;
      Swal.fire('Error', friendly, 'error');
    } finally { setBusy(false); }
  };

  return (
    <form className="ifrm" onSubmit={handleSubmit}>

      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaLock style={{color:'#10ac84'}} /> System Identifier</div>
        <div className="ifrm-field">
          <label className="ifrm-label">
            Taluka Code <span className="ifrm-badge-auto">Auto-generated</span>
          </label>
          {codeLoading
            ? <p className="ifrm-code-loading">Generating code…</p>
            : (
              <div className="ifrm-locked-wrap">
                <FaLock className="ifrm-lock-icon" />
                <input className="ifrm-input" value={code} disabled />
              </div>
            )
          }
        </div>
      </div>

      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaMapMarkerAlt style={{color:'#0f6fad'}} /> Taluka Details</div>

        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label">
              Taluka Name <span className="ifrm-required">*</span>
            </label>
            <input
              className={`ifrm-input${errors.name ? ' error' : ''}`}
              placeholder="e.g. Latifabad"
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({...p, name:''})); }}
              disabled={busy}
            />
            {errors.name && <p className="ifrm-error">{errors.name}</p>}
          </div>

          <div className="ifrm-field">
            <label className="ifrm-label">
              <FaToggleOn style={{color:'#64748b'}} /> Status
            </label>
            <select
              className="ifrm-select"
              value={isActive}
              onChange={e => setIsActive(e.target.value)}
              disabled={busy}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="ifrm-actions">
        <button type="button" className="ifrm-btn ifrm-btn-cancel" onClick={onClose} disabled={busy}>
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="ifrm-btn ifrm-btn-submit" disabled={busy || codeLoading}>
          {busy ? <><span className="ifrm-spinner" /> Saving…</> : <><FaSave /> {isEdit ? 'Update Taluka' : 'Create Taluka'}</>}
        </button>
      </div>
    </form>
  );
}

TalukaCreate.propTypes = { taluka: PropTypes.object, onClose: PropTypes.func, onSubmit: PropTypes.func };
export default TalukaCreate;
